/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { BlueprintContributions, ScopeQuestionGroup, ScopeQuestionOptionalValue } from '@g17eco/types/survey';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { CompletionModalProps, QuestionContainerStateless } from '@features/survey/question/QuestionContainerStateless';
import SurveyQuestionList, { SurveyQuestionListProps } from '@features/survey-question-list/survey-question-list';
import { useState } from 'react';
import { BulkActionToolbarProps } from '@features/survey-question-list/partials/BulkActionToolbar';
import { ColumnSimpleStatusIcon } from './partials/ColumnSimpleStatusIcon';
import { getColumnAnswerIndicator } from './partials/ColumnAnswerIndicator';
import { isMaterialityQuestionReadOnly } from './utils';
import { Column, Row } from '@g17eco/molecules/tracking-list';
import { ColumnTitle, ColumnTitleProps } from '@g17eco/molecules/survey-question-list';
import './style.scss';
import { BlockingLoader } from '../../atoms/loader';

const LOCKED_METRIC_MESSAGE =
  'Metric locked. These metrics have been answered during the assessments creation and cannot be updated. Please contact us for more information.';

interface ExpandingSurveyQuestionListProps
  extends Omit<SurveyQuestionListProps, 'goToQuestion' | 'surveyGroups' | 'unitConfig'> {
  survey: SurveyActionData;
  surveyGroups: ScopeQuestionGroup[];
  handleReload: () => Promise<void>;
  Toolbar?: (props: BulkActionToolbarProps) => JSX.Element | null;
  disabledUTRs: string[];
  expansionRowContent?: (question: ScopeQuestionOptionalValue) => JSX.Element | null;
  handleExpandRow?: (value: React.SetStateAction<ExpandingQuestionIds>) => void;
  /** Custom completion modal component to show when last question is answered */
  completionModal?: (props: CompletionModalProps) => JSX.Element;
}

export interface ExpandingQuestionIds {
  expandingId?: string;
  openedIds: Set<string>;
}

const getMTColumnTitle = (tooltip: string) => (props: ColumnTitleProps) => {
  return <ColumnTitle {...props} tooltip={tooltip} />;
};

export const ExpandingSurveyQuestionList = (props: ExpandingSurveyQuestionListProps) => {
  const { survey, surveyGroups, Toolbar, disabledUTRs, handleReload, handleExpandRow, completionModal } = props;
  const [questionIds, setQuestionIds] = useState<ExpandingQuestionIds>({
    openedIds: new Set(),
  });
  const [submittedUtrvId, setSubmittedUtrvId] = useState<string | undefined>();

  const toggleExpanded = (questionId: string | undefined) => {
    const expandFunction = (questionIds: ExpandingQuestionIds) => {
      const expandingId = questionId === questionIds.expandingId ? undefined : questionId;
      const openedIds = new Set(questionIds.openedIds);
      if (expandingId) {
        openedIds.add(expandingId);
      }
      return { expandingId, openedIds };
    };
    handleExpandRow?.(expandFunction);
    setQuestionIds(expandFunction);
  };

  const blueprint: BlueprintContributions = {};

  return (
    <SurveyQuestionList
      key={survey._id}
      {...props}
      unitConfig={survey.unitConfig}
      surveyGroups={surveyGroups}
      goToQuestion={(questionId) => toggleExpanded(questionId)}
      allowBulkActions={!!Toolbar}
      bulkActionToolbar={(props) => (Toolbar ? <Toolbar {...props} surveyData={survey} /> : null)}
      columns={props.columns ?? [ColumnSimpleStatusIcon, getMTColumnTitle(''), getColumnAnswerIndicator(questionIds.expandingId)]}
      // Since renderExpansionRow doesn’t reliably indicate the expanded row, use expandedRowId to apply styles instead.
      expandedRowId={questionIds.expandingId}
      submittedUtrvId={submittedUtrvId}
      renderExpansionRow={(question) => {
        const utrvId = question.utrv?._id ?? '';
        // We only render the row if it was open before to reduce number of preloaded rows.
        if (!questionIds.openedIds.has(utrvId)) {
          return null;
        }

        const isReadOnly = isMaterialityQuestionReadOnly({ code: question.universalTracker.getCode() });

        // @TODO - this will appear multiple times if a UTRV appears multiple times in survey,
        // should be fixed but not a priority for Materiality Tracker
        const isSubmittedUtrv = submittedUtrvId === utrvId;
        return (
          /** Show/hide by css to keep the question state of opened rows. https://worldwidegeneration.atlassian.net/browse/GU-6360 */
          <div
            style={{ display: utrvId !== questionIds.expandingId ? 'none' : 'block' }}
            className={`expansion-answer ${isSubmittedUtrv ? 'is-submitted' : ''}`}
            onAnimationEnd={(e) => {
              if (e.currentTarget === e.target) {
                setSubmittedUtrvId(undefined);
              }
            }}
          >
            <Row className='background-ThemeBgLight'>
              <Column fill noWrap={false}>
                {props.expansionRowContent?.(question) ?? (
                  <QuestionContainerStateless
                    initiativeId={props.initiativeId}
                    surveyId={props.surveyId}
                    questionId={utrvId}
                    questionIndex={''}
                    survey={survey}
                    surveyGroups={surveyGroups}
                    blueprint={blueprint}
                    searchIndex={undefined}
                    handleReload={handleReload}
                    config={{
                      enableFurtherExplanation: false,
                      enableComments: false,
                      enableQuestionSubtitle: false,
                      enableEvidence: false,
                      enableNANR: false,
                      enableMakePrivate: false,
                      enableAIAssistant: false,
                      enableHeader: false,
                      enableCompletedMessage: false,
                      enableDataHistory: false,
                      enableInformation: false,
                      enableSuccessMessage: false,
                    }}
                    isQuestionReadOnly={isReadOnly}
                    blockingLoader={<BlockingLoader isAbsolute />}
                    completionModal={completionModal}
                    postSubmit={() => setSubmittedUtrvId(utrvId)}
                  />
                )}
              </Column>
            </Row>
          </div>
        );
      }}
      getDisabledRowTooltip={(question) =>
        isMaterialityQuestionReadOnly({ code: question.universalTracker.getCode() }) ? LOCKED_METRIC_MESSAGE : undefined
      }
      disabledUTRs={disabledUTRs}
    />
  );
};
